import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import styles from "./styles.module.css";
import { HomeProductItemType } from "src/types/products";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import Product from './item/Item';
import { useProductsQuery } from "src/store/api/homeApiSlice";
import { useMemo } from "react";
import { isRTL } from "src/utils/global";
import { ErrorScreen } from "src/components";
import useWindowWidth from "src/hooks/useWindowWidth";


const HomeProducts = () => {
    const { t, i18n } = useTranslation("home");
    const { data, isLoading, isError, error } = useProductsQuery(undefined);
    const width = useWindowWidth();

    // ✅ Determine if there is real data
    const hasData = !!data && data.length > 0;

    // ✅ Determine how many products to show depending on width
    const displayCount = useMemo(() => {
        if (width <= 575) return 3;       // small screen
        if (width <= 991) return 6;       // medium screen
        return data?.length || 0;         // large screen, show all
    }, [width, data?.length]);

    // ✅ Adjust slick settings based on width
    const SETTINGS = useMemo(() => {
        const slidesToShow =
            width <= 575 ? 1 :
                width <= 991 ? 2 :
                    3;

        const slidesToScroll = slidesToShow;

        // If there's only 1 or 2 items, adjust settings accordingly
        const itemCount = data?.length || 0;

        return {
            infinite: itemCount > slidesToShow, // only infinite if more slides exist
            slidesToShow: Math.min(slidesToShow, itemCount || 1),
            slidesToScroll: Math.min(slidesToScroll, itemCount || 1),
            autoplay: itemCount > 1, // disable autoplay for single item
            autoplaySpeed: 3500,
            speed: 800,
            dots: hasData && itemCount > 1,
            arrows: false,
            responsive: [
                { breakpoint: 1199, settings: { slidesToShow: Math.min(3, itemCount || 1), slidesToScroll: Math.min(3, itemCount || 1) } },
                { breakpoint: 991, settings: { slidesToShow: Math.min(2, itemCount || 1), slidesToScroll: Math.min(2, itemCount || 1) } },
                { breakpoint: 575, settings: { slidesToShow: 1, slidesToScroll: 1 } }
            ]
        };
    }, [hasData, width, data?.length]);


    if (isError) {
        let status: number | string = 500;
        if ("status" in error) {
            status = error.status;
        }
        return <ErrorScreen status={status} />;
    }

    const renderProducts = () => {
        const visibleProducts = (data || []).slice(0, displayCount);

        if (isLoading) {
            return Array.from({ length: 3 }).map((_, i) => (
                <div key={`skeleton_${i}`} className="py-3 px-2">
                    <Product />
                </div>
            ));
        }

        return visibleProducts.map((product: HomeProductItemType) => (
            <div key={`product__${product.slug}`} className="py-3 px-2">
                <Product product={product} />
            </div>
        ));
    };

    return (
        hasData ? <section className={`HomeProducts-section ${styles.outerCon} ${isRTL() ? styles.rtl : ""}`}>
            <div className="container">
                <div className={styles.titleCon}>
                    <h2 className={styles.title}>{t("products.title")}</h2>
                    <Link className={styles.titleLink} to="/supplements">
                        <span>{t("products.more")}</span>

                        {i18n.language.includes("ar") ? (
                            <FaArrowLeft size={16} />
                        ) : (
                            <FaArrowRight size={16} />
                        )}
                    </Link>
                </div>

                <div className={styles.products}>
                    <Slider {...SETTINGS}>
                        {renderProducts()}
                    </Slider>
                </div>
            </div>
        </section> : <>
        </>
    );
};

export default HomeProducts;
